import React, { useContext, useEffect, useRef, useState } from 'react';
import { Linking, View, ScrollView } from 'react-native';
import { Button, ListItem, Image } from '@rneui/themed';
import PropTypes from 'prop-types';
import Loading from './Loading';
import { ViewMore } from './ViewMore';
import { H1, H3, H4, H5, Paragraph } from '../text';
import { fetchPage } from '../../common/Api';
import AppConstants from '../../common/Constants';
import AppContext, { AppActions, AppEvents } from '../../contexts/App';
import LayoutContext from '../../contexts/Layout';
import useAxios from '../../hooks/useAxios';

const DEFAULT_PAGE_SIZE = 20;

const SearchResults = props => {
    const { state, dispatch } = useContext(AppContext);
    const { layout } = useContext(LayoutContext);
    const [ fethcingMore, setFetchingMore ] = useState(false);
    const [results, setResults] = useState();
    const [pageNumber, setPageNumber] = useState(1);
    const searchRef = useRef();
    const { get } = useAxios();

    const close = () => {
        dispatch({
            type: AppActions.SEARCH_STATE_CHANGE,
            event: AppEvents.SEARCH_QUERY_CLOSED
        });

        if (searchRef?.current?.close) {
            searchRef?.current?.close();
        }
    };

    const handleViewMore = async () => {
        setFetchingMore(true);
        setPageNumber(pageNumber + 1);
    };

    useEffect(() => {
        const { event, term } = state.search;

        if (event?.equals(AppEvents.SEARCH_QUERY_SUBMITTED)) {
            if (!term?.equals(props.query)) {
                (async () => {
                    const response = await get(`${AppConstants.REACT_APP_SEARCH_BASE}${term}&pageNumber=${1}`);

                    if (response) {
                        if (layout.device.isMobile) {
                            dispatch({
                                type: AppActions.HAMBURGER_STATE_CHANGE,
                                state: AppEvents.HAMBURGER_STATE_CLOSED
                            });
                        }

                        setResults({
                            hits: response.hits,
                            query: state.search.term,
                            totalHits: response.total_hits
                        });
                    }
                })();
            }
        }
    }, [ props.query, state.search ]);

    useEffect(() => {
        if (results) {
            const { term } = state.search;

            (async () => {
                const response = await get(`${AppConstants.REACT_APP_SEARCH_BASE}${term}&pageNumber=${pageNumber}`);

                if (response) {
                    setResults(results => {
                        results.hits.push(...response.hits);
                        return results;
                    });
                    setFetchingMore(false);
                }
            })();
        }
    }, [ pageNumber, state.search ])

    if (!results) {
        return <Loading />;
    }

    const displayViewMore = DEFAULT_PAGE_SIZE * pageNumber < results?.totalHits;

    const renderHeading = () => {
        let paginationText = 'Displaying 0 of 0 results';

        if (results?.totalHits > 0 && results?.totalHits < DEFAULT_PAGE_SIZE) {
            paginationText = `Displaying ${pageNumber} of ${results?.totalHits}`;
        } else if (results?.hits?.length === results.totalHits) {
            paginationText = `Displaying ${results?.totalHits} of ${results?.totalHits}`;
        } else {
            paginationText = `Displaying ${DEFAULT_PAGE_SIZE * pageNumber} of ${results?.totalHits}`;
        }

        return (
            <View style={{ flexDirection: 'row', marginBottom: layout.styleGuide.spacer2 }}>
                <View style={{ marginRight: layout.styleGuide.spacer3 }}>
                    <H1 style={{ marginBottom: layout.styleGuide.spacer3 }} text='Search Results' />
                    <Paragraph text={paginationText} />
                </View>
                <Button
                    containerStyle={{ marginTop: 9 }}
                    icon={{
                        color: '#6b0410',
                        name: 'times',
                        size: 20,
                        type: 'font-awesome-5'
                    }}
                    onPress={close}
                    type='clear'
                />
            </View>
        );
    };

    const renderHit = (hit, index) => {
        const { url, title, highlight, images } = hit;

        const handlePress = async () => {
            if (url) {
                if (url.indexOf('http') === -1) {
                    let content;

                    if (content) {
                        await fetchPage(dispatch, content.rootPath);
                    }
                } else {
                    Linking.openURL(url);
                }
            }
        };

        return (
            <ListItem key={index} style={{ flex: 1, marginBottom: layout.styleGuide.spacer2 }}>
                {images?.main && <Image containerStyle={{ marginRight: layout.styleGuide.spacer7 }} source={{ uri: images.main }} style={{ height: 150, width: 150 }} />}
                <ListItem.Content>
                    <H3 text={title} />
                    <H5 style={{ color: '#7f868b' }} text={highlight.replace(/(<([^>]+)>)/gi, '')} />
                    <Button buttonStyle={{ paddingLeft: 0 }} onPress={() => handlePress(url)} title={<H4 style={{ color: '#153d6e' }} text='Read More' />} type='clear' />
                </ListItem.Content>
            </ListItem>
        );
    };

    if (results?.hits?.length > 0) {
        return (
            <View
                dataSet={{ componentName: 'SearchResults' }}
                style={{ flex: 1, marginBottom: layout.styleGuide.componentSeparator, paddingHorizontal: layout.styleGuide.spacer1, width: props.width }}
            >
                {renderHeading()}

                <ScrollView
                    contentContainerStyle={{ flexDirection: 'column', flexGrow: 1, marginBottom: !displayViewMore ? layout.styleGuide.spacer2 : undefined, marginHorizontal: layout.styleGuide.spacer1 }}
                    ref={searchRef}
                    showsVerticalScrollIndicator={true}
                >
                    {results.hits.map((hit, index) => renderHit(hit, index))}
                </ScrollView>
                {displayViewMore && (
                    <View style={{ marginBottom: layout.styleGuide.spacer2 * 2, marginTop: layout.styleGuide.spacer2 }}>
                        <ViewMore color='#a61a2b' handler={handleViewMore} text='more' />
                    </View>
                )}
                { fethcingMore && <Loading /> }
            </View>
        );
    } else {
        return (
            <View style={{ alignItems: 'center', justifyContent: 'center' }}>
                {renderHeading()}
                <H3 style={{ marginHorizontal: layout.device.os.equals('web') ? '10pt' : 10 }} text={`No results found for ${state?.search?.term || ''}`} />
            </View>
        );
    }
};

SearchResults.propTypes = {
    query: PropTypes.string,
    searchRef: PropTypes.shape({
        current: PropTypes.any
    })
};

export default SearchResults;
